'use client'

import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import Fullscreen from '@/components/ui/fullscreen'
import { Button } from '@/components/ui/button'
import { MarkdownFenceNotice } from '@/components/markdown-fence-notice'
import {
  FileText,
  Eye,
  Edit3,
  Save,
  RotateCcw,
  Download,
  Copy,
  CheckCircle,
  Minimize2,
} from 'lucide-react'
import {
  MarkdownEditorPreview,
  MarkdownPreviewStatus,
  useMarkdownPreview,
} from '@/components/markdown-editor-preview'
import { trackPlausibleEvent } from '@/lib/plausible'
import { copyTextToClipboard } from '@/lib/clipboard'
import type { DocumentExportSettings } from '@/lib/document-export-settings'

export interface MarkdownFullscreenViewerLabels {
  title?: string
  source?: string
  preview?: string
  mobileToggleToPreview?: string
  mobileToggleToSource?: string
  copied?: string
  copy?: string
  download?: string
  exit?: string
  editing?: string
  live?: string
  linesSuffix?: string
}

export interface MarkdownFullscreenViewerProps {
  open: boolean
  onClose: () => void
  markdown: string
  fileName?: string
  onSave?: (newMarkdown: string) => void
  onDownload?: (markdown: string) => void | Promise<void>
  allowEdit?: boolean
  downloadDisabled?: boolean
  labels?: MarkdownFullscreenViewerLabels
  previewExportSettings?: DocumentExportSettings
}

export default function MarkdownFullscreenViewer({
  open,
  onClose,
  markdown,
  fileName,
  onSave,
  onDownload,
  allowEdit = true,
  downloadDisabled = false,
  labels,
  previewExportSettings,
}: MarkdownFullscreenViewerProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editableMarkdown, setEditableMarkdown] = useState('')
  const [mobileView, setMobileView] = useState<'source' | 'preview'>('source')
  const [copySuccess, setCopySuccess] = useState(false)

  const currentMarkdown = useMemo(
    () => (isEditing ? editableMarkdown : markdown),
    [isEditing, editableMarkdown, markdown],
  )
  const preview = useMarkdownPreview(currentMarkdown, open)
  const previewDirection = previewExportSettings?.textDirection ?? 'auto'
  const previewFontSize = previewExportSettings
    ? `${previewExportSettings.fontSizePt}pt`
    : undefined

  // When opening, default to editing for immediate adjustments
  useEffect(() => {
    if (open && allowEdit) {
      setEditableMarkdown(markdown)
      setIsEditing(true)
    }
  }, [open, allowEdit, markdown])

  const lineCount = useMemo(
    () => currentMarkdown.split('\n').length,
    [currentMarkdown],
  )

  const l = useMemo<Required<MarkdownFullscreenViewerLabels>>(
    () => ({
      title: labels?.title ?? 'Fullscreen',
      source: labels?.source ?? 'Source',
      preview: labels?.preview ?? 'Preview',
      mobileToggleToPreview: labels?.mobileToggleToPreview ?? 'Preview',
      mobileToggleToSource: labels?.mobileToggleToSource ?? 'Source',
      copied: labels?.copied ?? 'Copied',
      copy: labels?.copy ?? 'Copy',
      download: labels?.download ?? 'Download',
      exit: labels?.exit ?? 'Exit',
      editing: labels?.editing ?? 'Editing',
      live: labels?.live ?? 'Live',
      linesSuffix: labels?.linesSuffix ?? 'lines',
    }),
    [labels],
  )

  // Copy
  const copyToClipboard = useCallback(async () => {
    try {
      const copied = await copyTextToClipboard(currentMarkdown)
      if (!copied) {
        console.error('copy failed')
        return
      }
      setCopySuccess(true)
      const id = setTimeout(() => setCopySuccess(false), 2000)
      // best-effort cleanup on unmount handled by new timers if reopened
      return () => clearTimeout(id)
    } catch (e) {
      console.error('copy failed', e)
    }
  }, [currentMarkdown])

  // Download
  const downloadMarkdown = useCallback(
    (text: string) => {
      if (!text) return
      trackPlausibleEvent('download_click', {
        event_category: 'download',
        event_label: 'md_fullscreen_button',
      })
      const blob = new Blob([text], { type: 'text/markdown' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const inferred = fileName
        ? fileName.replace(/\.(docx?|doc)$/i, '.md')
        : 'document.md'
      a.download = inferred
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
    [fileName],
  )

  // Scroll sync
  const sourceRef = useRef<HTMLPreElement | HTMLTextAreaElement | null>(null)
  const mobileSourceRef = useRef<HTMLTextAreaElement>(null)
  const previewRef = useRef<HTMLDivElement>(null)
  const isSyncingRef = useRef(false)

  const syncScroll = useCallback(
    (sourceEl: HTMLElement, targetEl: HTMLElement) => {
      if (isSyncingRef.current) return
      isSyncingRef.current = true
      const sTop = sourceEl.scrollTop
      const sHeight = sourceEl.scrollHeight - sourceEl.clientHeight
      const tHeight = targetEl.scrollHeight - targetEl.clientHeight
      if (sHeight > 0 && tHeight > 0) {
        targetEl.scrollTop = (sTop / sHeight) * tHeight
      }
      requestAnimationFrame(() => {
        isSyncingRef.current = false
      })
    },
    [],
  )

  const handleSourceScroll = useCallback(
    (e: React.UIEvent<HTMLPreElement | HTMLTextAreaElement>) => {
      if (!previewRef.current) return
      syncScroll(e.currentTarget, previewRef.current)
    },
    [syncScroll],
  )

  const handleClose = useCallback(() => {
    if (allowEdit && isEditing && editableMarkdown !== markdown) {
      onSave?.(editableMarkdown)
    }
    onClose()
  }, [allowEdit, editableMarkdown, isEditing, markdown, onClose, onSave])

  const handleDownload = useCallback(async () => {
    if (!currentMarkdown) return

    if (allowEdit && isEditing && currentMarkdown !== markdown) {
      onSave?.(currentMarkdown)
    }

    if (onDownload) {
      await onDownload(currentMarkdown)
      return
    }

    downloadMarkdown(currentMarkdown)
  }, [
    allowEdit,
    currentMarkdown,
    downloadMarkdown,
    isEditing,
    markdown,
    onDownload,
    onSave,
  ])

  return (
    <Fullscreen
      open={open}
      onClose={handleClose}
      showCloseButton
      title={
        <h2 className="flex items-center gap-1 text-sm font-semibold md:gap-2 md:text-xl">
          <FileText className="h-4 w-4 md:h-6 md:w-6" />
          <span className="hidden sm:inline">{l.title}</span>
          <span className="sm:hidden">
            {mobileView === 'source' ? l.source : l.preview}
          </span>
        </h2>
      }
      actions={
        <>
          {/* Mobile toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setMobileView(mobileView === 'source' ? 'preview' : 'source')
            }
            className="flex items-center gap-1 px-2 py-1 text-xs sm:hidden"
          >
            {mobileView === 'source' ? (
              <>
                <Eye className="h-3 w-3" />
                {l.mobileToggleToPreview}
              </>
            ) : (
              <>
                <FileText className="h-3 w-3" />
                {l.mobileToggleToSource}
              </>
            )}
          </Button>

          {/* Copy */}
          <Button
            variant="outline"
            size="sm"
            onClick={copyToClipboard}
            className={`px-2 py-1 text-xs md:px-3 md:py-2 ${
              mobileView === 'source'
                ? 'lg:inline-flex'
                : 'hidden lg:inline-flex'
            }`}
          >
            {copySuccess ? (
              <>
                <CheckCircle className="mr-1 h-3 w-3 text-green-600 md:h-4 md:w-4" />
                <span className="hidden md:inline">{l.copied}</span>
              </>
            ) : (
              <>
                <Copy className="mr-0 h-3 w-3 md:mr-1 md:h-4 md:w-4" />
                <span className="hidden md:inline">{l.copy}</span>
              </>
            )}
          </Button>

          {/* Download */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            disabled={downloadDisabled || !currentMarkdown.trim()}
            className={`px-2 py-1 text-xs md:px-3 md:py-2 ${
              mobileView === 'source'
                ? 'lg:inline-flex'
                : 'hidden lg:inline-flex'
            }`}
          >
            <Download className="mr-0 h-3 w-3 md:mr-1 md:h-4 md:w-4" />
            <span className="hidden md:inline">{l.download}</span>
          </Button>

          {/* Exit */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleClose}
            className="hidden items-center gap-1 px-2 py-1 text-xs sm:flex md:px-3 md:py-2"
          >
            <Minimize2 className="h-3 w-3 md:h-4 md:w-4" />
            <span className="hidden md:inline">{l.exit}</span>
          </Button>
        </>
      }
    >
      {/* Desktop: two-column; Mobile: switch single view */}
      <div className="flex h-full min-h-0 flex-col">
        {allowEdit && (
          <MarkdownFenceNotice
            markdown={currentMarkdown}
            onApply={(content) => {
              setEditableMarkdown(content)
              setIsEditing(true)
              setMobileView('source')
              requestAnimationFrame(() => {
                const mobileEditor = mobileSourceRef.current
                if (mobileEditor?.getClientRects().length) {
                  mobileEditor.focus()
                } else {
                  sourceRef.current?.focus()
                }
              })
            }}
          />
        )}
        {/* Desktop split */}
        <div className="hidden min-h-0 flex-1 gap-6 lg:grid lg:grid-cols-2">
          {/* Source panel */}
          <div className="flex min-h-0 flex-col rounded-xl bg-card p-6 shadow-lg">
            <div className="mb-4 flex flex-shrink-0 items-center justify-between">
              <h3 className="flex items-center gap-2 text-lg font-semibold">
                <FileText className="h-5 w-5" />
                {l.source}
                {isEditing && (
                  <span className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-600 dark:bg-blue-500/20 dark:text-blue-300">
                    {l.editing}
                  </span>
                )}
              </h3>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {lineCount} {l.linesSuffix}
                </span>
                {/* {allowEdit && !isEditing ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={startEditing}
                    className="h-8 w-8 p-0 hover:bg-gray-100"
                    title="Edit markdown"
                  >
                    <Edit3 className="h-4 w-4" />
                  </Button>
                ) : allowEdit ? (
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={saveChanges}
                      className="h-8 w-8 p-0 text-green-600 hover:bg-green-100"
                      title="Save changes"
                    >
                      <Save className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={discardChanges}
                      className="h-8 w-8 p-0 text-red-600 hover:bg-red-100"
                      title="Discard changes"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                  </div>
                ) : null} */}
              </div>
            </div>
            <div className="min-h-0 flex-1">
              {!isEditing ? (
                <pre
                  ref={sourceRef as React.RefObject<HTMLPreElement>}
                  dir={previewDirection}
                  className="h-full overflow-auto rounded-lg border border-border bg-muted p-4 font-mono text-sm"
                  style={{ WebkitOverflowScrolling: 'touch' }}
                  onScroll={handleSourceScroll}
                >
                  <code className="whitespace-break-spaces text-foreground">
                    {markdown}
                  </code>
                </pre>
              ) : (
                <textarea
                  ref={sourceRef as React.RefObject<HTMLTextAreaElement>}
                  dir={previewDirection}
                  value={editableMarkdown}
                  onChange={(e) => setEditableMarkdown(e.target.value)}
                  onScroll={handleSourceScroll}
                  className="h-full w-full resize-none rounded-lg border border-border bg-muted p-4 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ WebkitOverflowScrolling: 'touch' }}
                  placeholder="Edit your markdown here..."
                />
              )}
            </div>
          </div>

          {/* Preview panel */}
          <div className="flex min-h-0 flex-col rounded-xl bg-card p-6 shadow-lg">
            <div className="mb-4 flex flex-shrink-0 items-center justify-between">
              <h3 className="flex items-center gap-2 text-lg font-semibold">
                <Eye className="h-5 w-5" />
                {l.preview}
                <MarkdownPreviewStatus pending={preview.pending} />
                {isEditing && !preview.pending && (
                  <span className="rounded bg-muted px-2 py-1 text-xs text-green-600 dark:bg-green-500/20 dark:text-green-200">
                    {l.live}
                  </span>
                )}
              </h3>
            </div>
            <div className="min-h-0 flex-1">
              <div
                ref={previewRef}
                dir={previewDirection}
                className="markdown-preview prose prose-sm h-full max-w-none overflow-auto rounded-lg border border-border p-4 dark:prose-invert"
                style={{
                  WebkitOverflowScrolling: 'touch',
                  fontSize: previewFontSize,
                }}
              >
                <MarkdownEditorPreview markdown={preview.previewMarkdown} />
              </div>
            </div>
          </div>
        </div>

        {/* Mobile single-view switch */}
        <div className="min-h-0 flex-1 lg:hidden">
          {mobileView === 'source' ? (
            <div className="flex h-full min-h-0 flex-col rounded-xl bg-card p-3 shadow-lg">
              <div className="mb-3 flex flex-shrink-0 items-center justify-between">
                <h3 className="flex items-center gap-2 text-base font-semibold">
                  <FileText className="h-4 w-4" />
                  {l.source}
                  {isEditing && (
                    <span className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-600 dark:bg-blue-500/20 dark:text-blue-300">
                      {l.editing}
                    </span>
                  )}
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">
                    {lineCount} {l.linesSuffix}
                  </span>
                </div>
              </div>
              <div className="min-h-0 flex-1">
                {!isEditing ? (
                  <pre
                    dir={previewDirection}
                    className="h-full overflow-auto rounded-lg border border-border bg-muted p-3 font-mono text-xs"
                    style={{ WebkitOverflowScrolling: 'touch' }}
                  >
                    <code className="whitespace-break-spaces text-foreground">
                      {markdown}
                    </code>
                  </pre>
                ) : (
                  <textarea
                    ref={mobileSourceRef}
                    dir={previewDirection}
                    value={editableMarkdown}
                    onChange={(e) => setEditableMarkdown(e.target.value)}
                    onScroll={handleSourceScroll}
                    className="h-full w-full resize-none rounded-lg border border-border bg-muted p-3 font-mono text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ WebkitOverflowScrolling: 'touch' }}
                    placeholder="Edit your markdown here..."
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="flex h-full min-h-0 flex-col rounded-xl bg-card p-3 shadow-lg">
              <div className="mb-3 flex flex-shrink-0 items-center justify-between">
                <h3 className="flex items-center gap-2 text-base font-semibold">
                  <Eye className="h-4 w-4" />
                  {l.preview}
                  <MarkdownPreviewStatus pending={preview.pending} />
                </h3>
              </div>
              <div className="min-h-0 flex-1">
                <div
                  dir={previewDirection}
                  className="markdown-preview prose-xs prose h-full max-w-none overflow-auto rounded-lg border border-border bg-muted p-3 dark:prose-invert"
                  style={{
                    WebkitOverflowScrolling: 'touch',
                    fontSize: previewFontSize,
                  }}
                >
                  <MarkdownEditorPreview markdown={preview.previewMarkdown} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Fullscreen>
  )
}
